import { useEffect } from "react";
import { XIcon } from "./Icons";

export const cx = (...parts) => parts.filter(Boolean).join(" ");

/* ------------------------------------------------------------------ Spinner */

export function Spinner({ size = 16, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={cx("animate-spin", className)}
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="2.5"
        opacity="0.2"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------- Button */

const VARIANTS = {
  primary:
    "bg-gradient-to-b from-brand-400 to-brand-600 text-white shadow-btn-brand hover:from-brand-300 hover:to-brand-500 hover:-translate-y-px active:translate-y-0 active:from-brand-600 active:to-brand-700 disabled:from-ink-300 disabled:to-ink-400 disabled:shadow-none disabled:translate-y-0",
  secondary:
    "bg-white text-ink-700 ring-1 ring-inset ring-ink-200 shadow-card hover:bg-ink-50 hover:ring-ink-300 hover:shadow-card-hover active:bg-ink-100 disabled:text-ink-400 disabled:shadow-none",
  ghost:
    "text-ink-600 hover:bg-ink-200/60 hover:text-ink-900 active:bg-ink-200 disabled:text-ink-400",
  success:
    "bg-gradient-to-b from-emerald-400 to-emerald-600 text-white shadow-btn-success hover:from-emerald-300 hover:to-emerald-500 hover:-translate-y-px active:translate-y-0 active:from-emerald-600 active:to-emerald-700 disabled:from-ink-300 disabled:to-ink-400 disabled:shadow-none disabled:translate-y-0",
  danger:
    "bg-gradient-to-b from-red-500 to-red-600 text-white shadow-btn-danger hover:from-red-400 hover:to-red-500 hover:-translate-y-px active:translate-y-0 disabled:from-ink-300 disabled:to-ink-400 disabled:shadow-none disabled:translate-y-0",
  dark: "bg-gradient-to-b from-ink-700 to-ink-900 text-white shadow-lift hover:from-ink-600 hover:to-ink-800 active:from-ink-800 active:to-ink-950 disabled:from-ink-300 disabled:to-ink-400",
};

const SIZES = {
  sm: "h-8 px-3 text-[13px] gap-1.5 rounded-lg",
  md: "h-10 px-4 text-sm gap-2 rounded-lg",
  lg: "h-11 px-5 text-[15px] gap-2 rounded-xl",
};

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  icon: Icon,
  iconRight: IconRight,
  className = "",
  children,
  disabled,
  ...rest
}) {
  return (
    <button
      disabled={disabled || loading}
      className={cx(
        "inline-flex select-none items-center justify-center font-medium transition-all duration-150",
        "disabled:cursor-not-allowed disabled:shadow-none",
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...rest}
    >
      {loading ? (
        <Spinner size={size === "sm" ? 13 : 15} />
      ) : (
        Icon && <Icon size={size === "sm" ? 14 : 16} />
      )}
      {children}
      {IconRight && !loading && <IconRight size={size === "sm" ? 14 : 16} />}
    </button>
  );
}

/* -------------------------------------------------------------------- Badge */

const BADGE_TONES = {
  neutral: "bg-ink-100 text-ink-600 ring-ink-200",
  success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  warning: "bg-amber-50 text-amber-700 ring-amber-200",
  danger: "bg-red-50 text-red-700 ring-red-200",
  brand: "bg-brand-50 text-brand-700 ring-brand-200",
  info: "bg-sky-50 text-sky-700 ring-sky-200",
};

export function Badge({ tone = "neutral", icon: Icon, className = "", children }) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset",
        BADGE_TONES[tone],
        className
      )}
    >
      {Icon && <Icon size={11} strokeWidth={2.5} />}
      {children}
    </span>
  );
}

/* --------------------------------------------------------------------- Card */

export function Card({ className = "", children, ...rest }) {
  return (
    <div
      className={cx(
        "rounded-2xl border border-ink-200/70 bg-white shadow-card ring-1 ring-white/50",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({ title, description, actions, icon: Icon, tone }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-ink-200/70 px-5 py-4">
      <div className="flex min-w-0 items-center gap-3">
        {Icon && (
          <span
            className={cx(
              "grid h-9 w-9 shrink-0 place-items-center rounded-lg ring-1",
              tone || "bg-brand-50 text-brand-600 ring-brand-100"
            )}
          >
            <Icon size={17} />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="truncate text-[15px] font-semibold text-ink-900">
            {title}
          </h2>
          {description && (
            <p className="mt-0.5 truncate text-[13px] text-ink-500">
              {description}
            </p>
          )}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------- Modal */

export function Modal({
  open = true,
  onClose,
  title,
  description,
  icon: Icon,
  iconTone,
  size = "md",
  footer,
  children,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = {
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 animate-fade-in bg-ink-950/50 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cx(
          "relative flex max-h-[90vh] w-full animate-scale-in flex-col overflow-hidden rounded-2xl bg-white shadow-modal",
          widths[size]
        )}
      >
        <div className="flex items-start gap-3 border-b border-ink-200/70 px-6 py-5">
          {Icon && (
            <span
              className={cx(
                "grid h-10 w-10 shrink-0 place-items-center rounded-xl ring-1",
                iconTone || "bg-brand-50 text-brand-600 ring-brand-100"
              )}
            >
              <Icon size={19} />
            </span>
          )}
          <div className="min-w-0 flex-1 pt-0.5">
            <h3 className="text-base font-semibold text-ink-900">{title}</h3>
            {description && (
              <p className="mt-1 text-[13px] leading-relaxed text-ink-500">
                {description}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="-mr-1 -mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-400 transition hover:bg-ink-100 hover:text-ink-700"
            aria-label="Close"
          >
            <XIcon size={17} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2.5 border-t border-ink-200/70 bg-ink-50/60 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- EmptyState */

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      {Icon && (
        <div className="mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-ink-100 text-ink-400 ring-1 ring-ink-200">
          <Icon size={24} />
        </div>
      )}
      <h3 className="text-[15px] font-semibold text-ink-800">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-ink-500">
          {description}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ----------------------------------------------------------------- Skeleton */

export function Skeleton({ className = "" }) {
  return <div className={cx("skeleton", className)} />;
}

/* -------------------------------------------------------------------- Field */

export function Field({ label, hint, error, required, children }) {
  return (
    <div>
      {label && (
        <label className="field-label">
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="mt-1.5 text-[12px] text-red-600">{error}</p>
      ) : (
        hint && <p className="mt-1.5 text-[12px] text-ink-400">{hint}</p>
      )}
    </div>
  );
}
